import { faCopy, faPaste } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { decomposeColor } from '@material-ui/core';
import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import { adjustTextColor } from './adjustTextColor';
import { ColorPalette } from './ColorPalette';
import HexColorPicker from './HexColorPicker';
import HSLColorPicker from './HSLColorPicker';
import { SelectedColorState } from './PalettePage';
import RGBColorPicker from './RGBColorPicker';
import { rgbToHsl } from './rgbToHsl';

export interface ColorPickerSectionProps {
  paletteState: ColorPalette;
  selectedColorState: SelectedColorState;
  setPaletteState: (palette: ColorPalette) => void;
}

export const ColorPickerSection: React.FC<ColorPickerSectionProps> = ({
  paletteState,
  setPaletteState,
  selectedColorState,
}) => {
  const { openColor, rgbColor, hslColor } = useMemo(() => {
    const openColorScale = paletteState[selectedColorState.colorKey];
    if (!openColorScale) return {};
    const openColor = openColorScale[selectedColorState.colorIndex];
    if (!openColor) return {};

    const decomposedOpenColor = decomposeColor(openColor);
    const rgbColor = decomposedOpenColor.values;
    const hslColor = rgbToHsl(rgbColor[0], rgbColor[1], rgbColor[2]);

    return {
      openColor,
      rgbColor,
      hslColor,
    };
  }, [selectedColorState, paletteState]);

  // Changing color
  const handleChangeHexColor = (hexColor: string) =>
    setPaletteState({
      ...paletteState,
      [selectedColorState.colorKey]: paletteState[selectedColorState.colorKey].map(
        (oldColor, index) => (selectedColorState.colorIndex === index ? hexColor : oldColor)
      ),
    });

  // Copy state
  const [copiedColor, setCopiedColor] = useState<string>();
  const textColor = adjustTextColor(paletteState, 0.85, 1);

  return (
    <div>
      {openColor && (
        <div className="gap-4 flex flex-col">
          <div className="flex flex-row gap-4">
            <div
              className="w-40 h-40 rounded-md flex items-center justify-center select-none"
              style={{ backgroundColor: openColor, color: textColor }}
            >
              Text
            </div>
            <div className="flex flex-col gap-2">
              <VoxelButton
                endIcon={<FontAwesomeIcon icon={faCopy} size="lg" />}
                size="md"
                title="Copy"
                onClick={() => setCopiedColor(openColor)}
                color="light-gray"
              />
              <VoxelButton
                endIcon={<FontAwesomeIcon icon={faPaste} size="lg" />}
                disabled={copiedColor === undefined}
                size="md"
                title="Paste"
                onClick={() => handleChangeHexColor(copiedColor)}
                color="light-gray"
              />
            </div>
          </div>

          <div className="w-40">
            <HexColorPicker color={openColor} setColor={handleChangeHexColor} />
          </div>
          <div className="w-40">
            <RGBColorPicker color={openColor} setColor={handleChangeHexColor} rgbColor={rgbColor} />
          </div>

          <div className="w-64">
            <HSLColorPicker color={openColor} setColor={handleChangeHexColor} hslColor={hslColor} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPickerSection;
